/**
 * CohortAnalysisPage — cohort-definition list + create + matrix view.
 *
 * Sections:
 * 1. Create-cohort form (name, cohort_type, period_unit, period_count, anchor_ts)
 * 2. Cohorts list (select one to view its matrix)
 * 3. Matrix totals for the selected cohort
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCohortList,
	useCohortMatrix,
	useCreateCohort,
} from "./hooks";
import type { CohortType, PeriodUnit } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";

const COHORT_TYPES: ReadonlyArray<CohortType> = [
	"signup",
	"first_conversion",
	"acquisition_source",
];
const PERIOD_UNITS: ReadonlyArray<PeriodUnit> = ["day", "week", "month"];

export function CohortAnalysisPage() {
	const listQuery = useCohortList();
	const createMut = useCreateCohort();

	const [name, setName] = useState("");
	const [cohortType, setCohortType] = useState<CohortType>("signup");
	const [periodUnit, setPeriodUnit] = useState<PeriodUnit>("week");
	const [periodCount, setPeriodCount] = useState<string>("12");
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const matrixQuery = useCohortMatrix(selectedId);

	const cohorts = useMemo(
 () => listQuery.data?.cohorts ?? [],
 [listQuery.data],
	);

	const parsedPeriodCount = useMemo(() => {
 const n = Number(periodCount);
 if (!Number.isFinite(n) || n <= 0) return null;
 return Math.trunc(n);
	}, [periodCount]);

	const canSubmit =
 !!name.trim() &&
 parsedPeriodCount !== null &&
 !createMut.isPending;

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 if (!canSubmit || parsedPeriodCount === null) return;
 createMut.mutate(
 {
 name: name.trim(),
 cohort_type: cohortType,
 period_unit: periodUnit,
 period_count: parsedPeriodCount,
 },
 {
 onSuccess: () => {
 setName("");
 },
 },
 );
	};

	const formatTs = (ts: number | undefined): string => {
 if (!ts || !Number.isFinite(ts)) return "-";
 const d = new Date(ts * 1000);
 return d.toISOString().slice(0, 19).replace("T","");
	};

	return (
 <main
 data-testid="cohort-analysis-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 cohort-analysis
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Cohort Analysis
 </h1>
 <p className="max-w-2xl text-sm text-zinc-600">
 Segment performance by cohort over time. Define a cohort, register
 members, record events, and inspect the retention matrix.
 </p>
 </header>

 <section className={CARD} aria-label="Create cohort">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Create cohort
 </h2>
 <form
 onSubmit={handleCreate}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
 data-testid="cohort-analysis-create-form"
 >
 <label className="flex flex-col gap-1 lg:col-span-2">
 <span className="text-xs font-medium text-zinc-700">Name</span>
 <input
 type="text"
 className={INPUT}
 placeholder="e.g. Q1 launch wave"
 value={name}
 onChange={(e) => setName(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cohort-analysis-name-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className="text-xs font-medium text-zinc-700">Type</span>
 <select
 className={SELECT}
 value={cohortType}
 onChange={(e) =>
 setCohortType(e.currentTarget.value as CohortType)
 }
 disabled={createMut.isPending}
 data-testid="cohort-analysis-type-select"
 >
 {COHORT_TYPES.map((t) => (
 <option key={t} value={t}>
 {t}
 </option>
 ))}
 </select>
 </label>
 <label className="flex flex-col gap-1">
 <span className="text-xs font-medium text-zinc-700">
 Period unit
 </span>
 <select
 className={SELECT}
 value={periodUnit}
 onChange={(e) =>
 setPeriodUnit(e.currentTarget.value as PeriodUnit)
 }
 disabled={createMut.isPending}
 data-testid="cohort-analysis-unit-select"
 >
 {PERIOD_UNITS.map((u) => (
 <option key={u} value={u}>
 {u}
 </option>
 ))}
 </select>
 </label>
 <label className="flex flex-col gap-1">
 <span className="text-xs font-medium text-zinc-700">
 Period count
 </span>
 <input
 type="number"
 min={1}
 className={INPUT}
 value={periodCount}
 onChange={(e) => setPeriodCount(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cohort-analysis-count-input"
 />
 </label>
 <div className="flex items-end lg:col-span-4">
 <button
 type="submit"
 className={BTN}
 disabled={!canSubmit}
 data-testid="cohort-analysis-submit"
 >
 {createMut.isPending ? "Saving." : "Create cohort"}
 </button>
 </div>
 </form>
 {createMut.error ? (
 <p
 className="mt-2 text-xs text-red-600"
 role="alert"
 data-testid="cohort-analysis-create-error"
 >
 {createMut.error.message}
 </p>
 ) : null}
 </section>

 <section className={CARD} aria-label="Cohorts list">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Cohorts</h2>
 {listQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cohort-analysis-loading"
 >
 Loading.
 </p>
 ) : listQuery.error ? (
 <p
 className="mt-3 text-sm text-red-600"
 role="alert"
 data-testid="cohort-analysis-list-error"
 >
 {listQuery.error.message}
 </p>
 ) : cohorts.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cohort-analysis-empty"
 >
 No cohorts yet. Create your first above.
 </p>
 ) : (
 <ul
 className="mt-3 divide-y divide-zinc-200"
 data-testid="cohort-analysis-list"
 >
 {cohorts.map((c) => (
 <li
 key={c.id}
 className="flex items-center justify-between gap-3 py-2 text-sm"
 data-testid={`cohort-analysis-row-${c.id}`}
 >
 <div className="flex min-w-0 flex-col">
 <span className="truncate font-medium text-zinc-900">
 {c.name}
 </span>
 <span className="truncate text-xs text-zinc-500">
 {c.cohort_type} / {c.period_unit} x {c.period_count} /
 anchor {formatTs(c.anchor_ts)}
 </span>
 </div>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => setSelectedId(c.id)}
 data-testid={`cohort-analysis-select-${c.id}`}
 >
 View matrix
 </button>
 </li>
 ))}
 </ul>
 )}
 </section>

 {selectedId ? (
 <section className={CARD} aria-label="Selected cohort matrix">
 <div className="flex items-start justify-between gap-3">
 <div>
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Matrix
 </h2>
 <p className="mt-1 font-mono text-xs text-zinc-500">
 {selectedId}
 </p>
 </div>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => setSelectedId(null)}
 data-testid="cohort-analysis-clear-selection"
 >
 Clear
 </button>
 </div>

 {matrixQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cohort-analysis-matrix-loading"
 >
 Loading matrix.
 </p>
 ) : matrixQuery.error ? (
 <p
 className="mt-3 text-sm text-red-600"
 role="alert"
 data-testid="cohort-analysis-matrix-error"
 >
 {matrixQuery.error.message}
 </p>
 ) : matrixQuery.data ? (
 <div
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3"
 data-testid="cohort-analysis-matrix-totals"
 >
 <div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
 <p className="text-xs font-medium text-zinc-500">Members</p>
 <p className="mt-1 text-2xl font-semibold text-zinc-900">
 {matrixQuery.data.totals.members}
 </p>
 </div>
 <div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
 <p className="text-xs font-medium text-zinc-500">
 Retained users
 </p>
 <p className="mt-1 text-2xl font-semibold text-zinc-900">
 {matrixQuery.data.totals.retained_users}
 </p>
 </div>
 <div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
 <p className="text-xs font-medium text-zinc-500">
 Revenue (cents)
 </p>
 <p className="mt-1 text-2xl font-semibold text-zinc-900">
 {matrixQuery.data.totals.revenue_cents}
 </p>
 </div>
 </div>
 ) : null}
 </section>
 ) : null}
 </main>
	);
}

export default CohortAnalysisPage;
